import UserProfileDetailsInfosItemBan from './UserProfileDetailsInfosItemBan';
import UserProfileDetailsInfosItem from './UserProfileDetailsInfosItem';
import { Cake, Mail, Plus, Trash, VenetianMask } from 'lucide-react';
import { IHasClassName, IHasUserData } from '@/interfaces';
import { getAge, getTitleEnumValue } from '@/functions';
import BadgeUserCertified from '../BadgeUserCertified';
import { cn, getUserCoinCount } from '@/lib/utils.ts';
import NbrOfAccountsText from '../NbrOfAccountsText';
import IconCoins from '../IconCoins';
import IconFlag from '../IconFlag';
import { dayjs } from '@/config';
import { TAlign } from '.';

type TProps = IHasClassName & IHasUserData & {
	hideDevicesBanned?: boolean;
	hideDevicesCount?: boolean;
	hideIncognito?: boolean;
	hideCountry?: boolean;
	hideEmail?: boolean;
	hideCoins?: boolean;
	hideAge?: boolean;
	align?: TAlign;
}

const UserProfileDetailsInfos = ({
	hideDevicesBanned,
	hideDevicesCount,
	hideIncognito,
	hideCountry,
	hideCoins,
	hideEmail,
	className,
	userData,
	hideAge,
	align = 'left'
}: TProps) => {

	const coins = getUserCoinCount(userData?.coins || 0, userData?.diamondWallet?.coins);

	if (!userData) return null;

	return (
		<div className={cn(
			'flex flex-col space-y-1 px-5',
			className
		)}>
			{/* Country */}
			{
				!hideCountry &&
				userData.countryIso3 &&
				<UserProfileDetailsInfosItem align={align}>
					<IconFlag
						className='h-3'
						country={userData.countryIso3} />
					<span>
						{getTitleEnumValue('CountryEnum', userData.countryIso3)}
					</span>
				</UserProfileDetailsInfosItem>
			}
			{/* Age */}
			{
				!hideAge &&
				<UserProfileDetailsInfosItem align={align}>
					<Cake size={16} />
					<span>
						{getAge(userData.birthdate)} ans
					</span>
				</UserProfileDetailsInfosItem>
			}
			{/* Email */}
			{
				!hideEmail &&
				<UserProfileDetailsInfosItem align={align}>
					<Mail size={16} />
					<span>
						{userData.email}
					</span>
				</UserProfileDetailsInfosItem>
			}
			{/* Coins */}
			{
				!hideCoins &&
				<UserProfileDetailsInfosItem align={align}>
					<IconCoins />
					<span>
						{coins.toLocaleString('fr-FR')} Pièce(s)
					</span>
				</UserProfileDetailsInfosItem>
			}
			{/* Incognito */}
			{
				!hideIncognito &&
				!!userData?.incognito &&
				<UserProfileDetailsInfosItem align={align}>
					<VenetianMask
						size={16}
						className='text-purple-600' />
					<span>
						Possède le mode incognito
					</span>
				</UserProfileDetailsInfosItem>
			}
			{/* Ban */}
			{
				!hideDevicesBanned &&
				<UserProfileDetailsInfosItemBan
					user={userData}
					align={align} />
			}
			{
				!hideDevicesCount &&
				userData?._id &&
				<NbrOfAccountsText
					align={align}
					userId={userData._id}
					deviceAccountsCount={userData.deviceAccountsCount} />
			}
			{/* Certified */}
			{
				userData?.verifications?.id?.reviewer?.name &&
				<UserProfileDetailsInfosItem align={align}>
					<BadgeUserCertified
						forceDisplay
						size={16} />
					<p className='flex items-center space-x-1'>
						<span>
							Certifié par
						</span>
						<span className='text-purple-600'>
							{userData.verifications?.id?.reviewer?.name}
						</span>
						{
							userData.verifications?.id?.verifiedAt &&
							<span
								className='cursor-pointer'
								title={dayjs(userData.verifications.id.verifiedAt).format('DD/MM/YYYY hh:mm:ss')}>
								{dayjs().locale('fr').to(dayjs(userData.verifications?.id?.verifiedAt))}
							</span>
						}
					</p>
				</UserProfileDetailsInfosItem>
			}
			{/* Deleted */}
			{
				userData.deletedBy &&
				<UserProfileDetailsInfosItem align={align}>
					<Trash
						size={16}
						className='text-red-500' />
					<p className='flex items-center space-x-1'>
						<span>
							Supprimé par
						</span>
						<span className='text-purple-600'>
							{userData.deletedBy}
						</span>
						{
							userData.deletedAt &&
							<span
								className='cursor-pointer'
								title={dayjs(userData.deletedAt).format('DD/MM/YYYY hh:mm:ss')}>
								{dayjs().locale('fr').to(dayjs(userData.deletedAt))}
							</span>
						}
					</p>
				</UserProfileDetailsInfosItem>
			}
			{/* Member since */}
			<UserProfileDetailsInfosItem align={align}>
				<Plus size={16} />
				<span
					className='cursor-pointer'
					title={dayjs(userData.createdAt).format('DD/MM/YYYY hh:mm:ss')}>
					Membre depuis {(
						dayjs(userData.createdAt)
							.locale('fr')
							.format('MMMM YYYY')
					)}
				</span>
			</UserProfileDetailsInfosItem>
		</div>
	);

};

export default UserProfileDetailsInfos;
